import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SharedTabButton from 'shared/components/buttons/tab-button/tab-button';
import PageHeader from 'shared/components/page-header/page-header';
import PageHeaderSkeleton from 'shared/components/page-header/page-header-skeleton';
import { useDocument } from 'core/hooks/useDocument';
import { IRunActivityDao } from 'core/api/types/run-activity.interface';
import { RunActivityTab, runActivityTabs } from 'core/constants/run-activity-tab';
import {
  ConsignmentsApiService,
  RunActivityApiService,
  ShiftActivityApiService,
  VehicleChecksApiService,
} from 'core/api';
import dayjs from 'dayjs';
import { ISharedTableColumn, ISharedTableCustomCellTemplate } from 'shared/components/table/table-interface';
import { where } from 'firebase/firestore';
import { IVehicleCheckDao } from 'core/api/types/vehicle-checks.interface';
import { useCollection } from 'core/hooks/useCollection';
import SharedTable from 'shared/components/table/table';
import { IShiftActivityDao } from 'core/api/types/shift-activity.interface';
import SharedButton from 'shared/components/buttons/button';

import { IConsignmentDao } from 'core/api/types/consignments.interface';
import { useDialog } from 'core/providers/DialogProvider';
import ViewLocationDialog from './dialogs/view-location-dialog';
import { useSharedData } from 'core/providers/SharedDataProvider';

const VehicleChecksTableColumns: ISharedTableColumn[] = [
  { label: 'Direction', key: 'direction', templateId: 'direction', width: 10 },
  { label: 'Type', key: 'type', templateId: 'type', width: 10 },
  { label: 'Check Date', key: 'checkDate', templateId: 'checkDate', width: 10 },
  { label: 'Mileage', key: 'mileage', templateId: 'text', width: 10 },
  { label: 'Vehicle Registration', key: 'vehicleRegistration', templateId: 'vehicleRegistration', width: 10 },
  { label: 'Driver', key: 'driver', templateId: 'driver', width: 10 },
  { label: '', key: 'action', templateId: 'action', width: 10 },
];

const ShiftActivityTableColumns: ISharedTableColumn[] = [
  { label: 'Location', key: 'location', templateId: 'location', width: 19 },
  { label: 'Sub Location', key: 'subLocation', templateId: 'subLocation', width: 19 },
  { label: 'Process Types', key: 'processTypes', templateId: 'processTypes', width: 19 },
  { label: 'Completed at ', key: 'completedAt', templateId: 'completedAt', width: 19 },
  { label: 'Status', key: 'status', templateId: 'status', width: 19 },
  { label: '', key: 'action', templateId: 'action', width: 5 },
];

const ConsignmentsTableColumns: ISharedTableColumn[] = [
  { label: 'Collection Location', key: 'collectionLocation', templateId: 'collectionLocation', width: 10 },
  { label: 'Delivery Location', key: 'deliveryLocation', templateId: 'deliveryLocation', width: 10 },
  { label: 'Process Name', key: 'processName', templateId: 'processName', width: 10 },
  { label: 'Collection Sub Location', key: 'collectionSubLocation', templateId: 'collectionSubLocation', width: 10 },
  { label: 'Delivery Sub Location', key: 'deliverySubLocation', templateId: 'deliverySubLocation', width: 10 },
  { label: 'Collection Time', key: 'collectionTime', templateId: 'collectionTime', width: 10 },
  { label: 'Delivery Time', key: 'deliveryTime', templateId: 'deliveryTime', width: 10 },
  { label: 'Status', key: 'status', templateId: 'status', width: 10 },
  { label: '', key: 'action', templateId: 'action', width: 10 },
];

const RunActivityItem = () => {
  const { uid } = useParams();
  const navigate = useNavigate();
  const dialog = useDialog();
  const { vehicles, drivers, deliveryMeta } = useSharedData();

  const [selectedTab, setSelectedTab] = useState<string>(RunActivityTab.SHIFT_ACTIVITY);

  const conisgnmentsConstraints = useMemo(() => [where('runActivityId', '==', uid)], [uid]);
  const vehicleChecksConstraints = useMemo(() => [where('runActivityUid', '==', uid)], [uid]);
  const shiftActivityConstraints = useMemo(() => [where('runActivityId', '==', uid)], [uid]);

  const { tableRows: consignments, loading: consignmentsLoading } = useCollection<IConsignmentDao>({
    apiService: ConsignmentsApiService,
    constraints: conisgnmentsConstraints,
    initialLimit: 100,
  });

  const { tableRows: vehicleChecks, loading: vehicleChecksLoading } = useCollection<IVehicleCheckDao>({
    apiService: VehicleChecksApiService,
    constraints: vehicleChecksConstraints,
    initialLimit: 100,
  });

  const { tableRows: shiftActivity, loading: shiftActivityLoading } = useCollection<IShiftActivityDao>({
    apiService: ShiftActivityApiService,
    constraints: shiftActivityConstraints,
    initialLimit: 100,
  });

  const { data: runActivity, loading: runActivityLoading } = useDocument<IRunActivityDao>({
    apiService: RunActivityApiService,
    uid,
    navigateTo: '/run-activity',
  });

  const locationCellTemplate = (shiftActivity: IShiftActivityDao) => {
    return <p>{shiftActivity.location.name || '-'}</p>;
  };

  const subLocationCellTemplate = (shiftActivity: IShiftActivityDao) => {
    return <p>{shiftActivity.subLocation?.name || '-'}</p>;
  };

  const processTypesCellTemplate = (shiftActivity: IShiftActivityDao) => {
    return <p>{shiftActivity.processTypes.map((processType) => processType.name).join(', ') || '-'}</p>;
  };

  const consignmentStatusCellTemplate = (consignment: IConsignmentDao) => {
    const { status } = consignment;
    let text: string = 'In Progress';
    let color: string = '#6b7280';

    if (status === 'complete') {
      text = 'Complete';
      color = '#16a34a';
    } else if (status === 'incomplete') {
      text = 'Incomplete';
      color = '#ef4444';
    }

    return (
      <div
        className='w-fit px-2 py-1 rounded-lg border text-xs'
        style={{ backgroundColor: `${color}0D`, borderColor: color, color: color }}
      >
        {text}
      </div>
    );
  };

  const statusCellTemplate = (shiftActivity: IShiftActivityDao) => {
    const { status } = shiftActivity;
    let text: string = 'Pending';
    let color: string = '#6b7280';
    if (status === 'success') {
      text = 'Complete';
      color = '#16a34a';
    } else if (status !== null) {
      const statusLookup = deliveryMeta?.locationStatus?.find((ls) => ls.key === status);
      if (statusLookup) {
        text = statusLookup.name;
        color = '#ef4444';
      } else {
        text = 'Unknown';
      }
    }

    return (
      <div
        className='w-fit px-2 py-1 rounded-lg border text-xs'
        style={{ backgroundColor: `${color}0D`, borderColor: color, color: color }}
      >
        {text}
      </div>
    );
  };

  const completedAtCellTemplate = (shiftActivity: IShiftActivityDao) => {
    return (
      <p>
        {shiftActivity.status !== null ? dayjs(shiftActivity.updated.at.toDate()).format('HH:mm, DD/MM/YYYY') : '-'}
      </p>
    );
  };

  const shiftActivityActionCellTemplate = (shiftActivity: IShiftActivityDao) => {
    return (
      <div className='w-full flex justify-end space-x-2'>
        <SharedButton
          onClick={() => navigate(`shift-activity/${shiftActivity.uid}`)}
          type='button'
          appearance='subtle'
          spacing='compact'
          label='View'
        />
        {shiftActivity.submissionLocation && (
          <SharedButton
            onClick={() => dialog?.openDialog(<ViewLocationDialog position={shiftActivity.submissionLocation} />)}
            type='button'
            appearance='subtle'
            spacing='compact'
            label='View Location'
          />
        )}
      </div>
    );
  };

  const shiftActivityCustomTemplates: ISharedTableCustomCellTemplate[] = [
    {
      template: locationCellTemplate,
      id: 'location',
    },
    {
      template: subLocationCellTemplate,
      id: 'subLocation',
    },
    {
      template: processTypesCellTemplate,
      id: 'processTypes',
    },
    {
      template: statusCellTemplate,
      id: 'status',
    },
    {
      template: completedAtCellTemplate,
      id: 'completedAt',
    },
    {
      template: shiftActivityActionCellTemplate,
      id: 'action',
    },
  ];

  const vehicleChecksActionCellTemplate = (vehicleCheck: IVehicleCheckDao) => (
    <div className='w-full flex justify-end space-x-2'>
      <SharedButton
        onClick={() => navigate(`/vehicle-checks/${vehicleCheck.uid}`)}
        type='button'
        appearance='subtle'
        spacing='compact'
        label='View'
      />
    </div>
  );

  const directionCellTemplate = (vehicleCheck: IVehicleCheckDao) => {
    return <p>{vehicleCheck.direction === 'in' ? 'In' : 'Out'}</p>;
  };

  const checkTypeCellTemplate = (vehicleCheck: IVehicleCheckDao) => {
    return <p>{vehicleCheck.type === 'daily' ? 'Daily' : 'Weekly'}</p>;
  };

  const checkDateCellTemplate = (vehicleCheck: IVehicleCheckDao) => {
    return <p>{dayjs(vehicleCheck.checkDate.toDate()).format('HH:mm, DD/MM/YYYY')}</p>;
  };

  const vehicleRegistrationCellTemplate = (vehicleCheck: IVehicleCheckDao) => {
    return <p>{vehicles?.find((vehicle) => vehicle.uid === vehicleCheck.vehicleUid)?.registration}</p>;
  };

  const driverCellTemplate = (vehicleCheck: IVehicleCheckDao) => {
    return <p>{drivers?.find((driver) => driver.uid === vehicleCheck.driver.uid)?.fullName}</p>;
  };

  const vehicleChecksCustomTemplates: ISharedTableCustomCellTemplate[] = [
    {
      template: directionCellTemplate,
      id: 'direction',
    },
    {
      template: checkTypeCellTemplate,
      id: 'type',
    },
    {
      template: vehicleRegistrationCellTemplate,
      id: 'vehicleRegistration',
    },
    {
      template: driverCellTemplate,
      id: 'driver',
    },
    {
      template: vehicleChecksActionCellTemplate,
      id: 'action',
    },
    { template: checkDateCellTemplate, id: 'checkDate' },
  ];

  const collectionLocationCellTemplate = (consignment: IConsignmentDao) => {
    return <p>{consignment.origin.location.name || '-'}</p>;
  };

  const deliveryLocationCellTemplate = (consignment: IConsignmentDao) => {
    return <p>{consignment.destination.location.name || '-'}</p>;
  };

  const collectionSubLocationCellTemplate = (consignment: IConsignmentDao) => {
    return <p>{consignment.origin.subLocation?.name || '-'}</p>;
  };

  const deliverySubLocationCellTemplate = (consignment: IConsignmentDao) => {
    return <p>{consignment.destination.subLocation?.name || '-'}</p>;
  };

  const collectionTimeCellTemplate = (consignment: IConsignmentDao) => {
    const date = consignment.origin.completion?.timestamp.at.toDate();
    return <p>{date ? dayjs(date).format('HH:mm, DD/MM/YYYY') : '-'}</p>;
  };

  const deliveryTimeCellTemplate = (consignment: IConsignmentDao) => {
    const date = consignment.destination.completion?.timestamp.at.toDate();
    return <p>{date ? dayjs(date).format('HH:mm, DD/MM/YYYY') : '-'}</p>;
  };

  const processNameCellTemplate = (consignment: IConsignmentDao) => {
    return <p>{consignment.processType.name || '-'}</p>;
  };

  const consigmentActionCellTemplate = (consignment: IConsignmentDao) => {
    return (
      <div className='w-full flex justify-end space-x-2'>
        {consignment.origin.completion?.position && (
          <SharedButton
            onClick={() =>
              dialog?.openDialog(<ViewLocationDialog position={consignment.origin.completion?.position} />)
            }
            type='button'
            appearance='subtle'
            spacing='compact'
            label='View Collection'
          />
        )}
        {consignment.destination.completion?.position && (
          <SharedButton
            onClick={() =>
              dialog?.openDialog(<ViewLocationDialog position={consignment.destination.completion?.position} />)
            }
            type='button'
            appearance='subtle'
            spacing='compact'
            label='View Delivery'
          />
        )}
      </div>
    );
  };

  const consignmentsCustomTemplates: ISharedTableCustomCellTemplate[] = [
    {
      template: collectionLocationCellTemplate,
      id: 'collectionLocation',
    },
    {
      template: deliveryLocationCellTemplate,
      id: 'deliveryLocation',
    },
    {
      template: collectionSubLocationCellTemplate,
      id: 'collectionSubLocation',
    },
    {
      template: deliverySubLocationCellTemplate,
      id: 'deliverySubLocation',
    },
    {
      template: processNameCellTemplate,
      id: 'processName',
    },
    {
      template: collectionTimeCellTemplate,
      id: 'collectionTime',
    },
    {
      template: deliveryTimeCellTemplate,
      id: 'deliveryTime',
    },
    {
      template: consignmentStatusCellTemplate,
      id: 'status',
    },
    {
      template: consigmentActionCellTemplate,
      id: 'action',
    },
  ];

  const getTabContent = () => {
    switch (selectedTab) {
      case RunActivityTab.SHIFT_ACTIVITY:
        return (
          <>
            <SharedTable
              columns={ShiftActivityTableColumns}
              rows={shiftActivity.sort((a, b) => a.data.index - b.data.index)}
              isLoading={shiftActivityLoading}
              customTemplates={shiftActivityCustomTemplates}
            />
          </>
        );
      case RunActivityTab.CONSIGNMENTS:
        return (
          <>
            <SharedTable
              columns={ConsignmentsTableColumns}
              rows={consignments.sort(
                (a, b) =>
                  a.data.origin.completion!.timestamp.at.seconds - b.data.origin.completion!.timestamp.at.seconds
              )}
              isLoading={consignmentsLoading}
              customTemplates={consignmentsCustomTemplates}
            />
          </>
        );
      case RunActivityTab.VEHICLE_CHECKS:
        return (
          <>
            <SharedTable
              columns={VehicleChecksTableColumns}
              rows={vehicleChecks.sort((a, b) => b.data.checkDate.seconds - a.data.checkDate.seconds)}
              isLoading={vehicleChecksLoading}
              customTemplates={vehicleChecksCustomTemplates}
            />
          </>
        );
    }
  };

  const RunActivityItemDetails = ({ runActivity }: { runActivity: IRunActivityDao }) => {
    const RunActivityDetails = [
      {
        label: 'Name',
        value: runActivity.name,
        key: 'name',
      },
      {
        label: 'Driver',
        value: drivers?.find((driver) => driver.uid === runActivity.currentDriver?.uid)?.fullName ?? 'Unknown',
        key: 'driver',
      },
      {
        label: 'Vehicle',
        value:
          vehicles?.find((vehicle) => vehicle.uid === runActivity.driverHistory?.[0]?.vehicleUid)?.registration ??
          'Unknown',
        key: 'registration',
      },
      {
        label: 'Created at',
        value: runActivity.created.at ? dayjs(runActivity.created.at.toDate()).format('HH:mm, DD/MM/YYYY') : 'Unknown',
        key: 'createdAt',
      },
      {
        label: 'Created by',
        value: runActivity.created.by.fullName ?? 'Unknown',
        key: 'createdBy',
      },
    ];

    return (
      <div className='flex-grow-0 flex-shrink-0 basis-[300px] bg-white shadow-md rounded-md h-fit'>
        <div className='border-b p-4 flex justify-between items-center'>
          <p className='headline-06'>Details</p>
        </div>
        <div className='p-4'>
          {RunActivityDetails.map((section) => (
            <div key={section.key} className='mt-4 first:mt-0'>
              <p className='body-02 text-gray-500'>{section.label}</p>
              <p className='body-01 break-words'>{section.value}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <>
      <>
        {!runActivity || runActivityLoading ? (
          <PageHeaderSkeleton actions={[]} subtitle={true} />
        ) : (
          <>
            <PageHeader title={`${runActivity.name}`} showBack={true} />
            <div className='flex flex-row py-6 space-x-6'>
              <RunActivityItemDetails runActivity={runActivity} />
              <div className='flex-grow min-w-0'>
                <div className='relative'>
                  <div className='flex relative z-10'>
                    {runActivityTabs.map((tab) => (
                      <SharedTabButton key={tab.key} changeTab={setSelectedTab} tab={tab} currentTab={selectedTab} />
                    ))}
                  </div>
                  <div className='absolute bottom-0 left-0 right-0 h-px bg-gray-200' />
                </div>
                <div className='mt-6'>{getTabContent()}</div>
              </div>
            </div>
          </>
        )}
      </>
    </>
  );
};

export default RunActivityItem;
